import { useState, useEffect } from "react";
import {
  Dialog,
  DialogSurface,
  DialogTitle,
  DialogBody,
  DialogActions,
  Button,
  makeStyles,
  Spinner,
  Checkbox,
  Tooltip,
} from "@fluentui/react-components";
import { Checkmark20Regular, Link16Regular } from "@fluentui/react-icons";
import { useTranslation } from "react-i18next";
import { Tool } from "../../models/Tool";
import { getDocumentIcon, getOpenLink } from "@skanska/vsa-file-icon";
import { useGetAllCurrentPublishedTools } from "../../hooks/useTools";
import {
  TextSortAscending24Regular,
  TextSortDescending24Regular,
} from "@fluentui/react-icons";
import { VsaSearchBox } from "../VsaSearchBox";
import { Paginate } from "../Paginate";
import { QueryFilter } from "../../models/QueryFilter";
import { sanitizeFileName } from "../../services/ToolService";

const useStyles = makeStyles({
  root: {
    display: "flex",
    flexDirection: "column",
    gap: "16px",
    margin: "16px",
    minHeight: "400px",
  },
  dialogtitle: {
    marginLeft: "16px",
  },
  toolContainer: {
    display: "flex",
    flexDirection: "column",
    gap: "8px",
    flex: 1,
    minHeight: "0",
  },
  searchRow: {
    display: "flex",
    alignItems: "center",
    alignContent: "center",
    gap: "8px",
  },
  toolList: {
    overflowY: "auto",
    flex: 1,
    minHeight: "0",
  },
  checkBoxGroup: {
    borderBottom: "1px solid #ddd",
    paddingBottom: "8px",
  },
  toolItem: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    padding: "6px 8px",
    cursor: "pointer",
    borderBottom: "1px solid #ddd",
    "&:hover": {
      backgroundColor: "#eee",
    },
  },
  toolName: {
    display: "flex",
    alignItems: "center",
    gap: "8px",
  },
  alignStart: {
    alignSelf: "start",
  },
  toolIcon: {
    width: "16px",
    height: "16px",
  },
});

type LinkDialogProps = {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onApply: (url: string, fullTool?: Tool) => void;
  currentLink?: string | null;
  selectedText?: string;
  selectedTools?: Tool[];
};

export default function LinkDialog({
  open,
  onOpenChange,
  onApply,
  selectedTools = [],
}: LinkDialogProps) {
  const classes = useStyles();
  const { t } = useTranslation();

  const {
    data: toolsResult,
    isLoading,
    isRefetching,
    setSortByAscending,
    setQuery,
    setCurrentPage,
    setFilters,
    sortByAscending,
    query,
  } = useGetAllCurrentPublishedTools("title", true, [
    {
      fieldUniqueName: "active",
      values: ["true"],
    },
  ]);

  const [showDocuments, setShowDocuments] = useState(true);
  const [showLinks, setShowLinks] = useState(true);

  useEffect(() => {
    const newFilters: QueryFilter[] = [
      {
        fieldUniqueName: "active",
        values: ["true"],
      },
    ];

    if (showDocuments && !showLinks) {
      newFilters.push({
        fieldUniqueName: "toolTypeLinkOrDocument",
        values: ["Document"],
      });
    } else if (!showDocuments && showLinks) {
      newFilters.push({
        fieldUniqueName: "toolTypeLinkOrDocument",
        values: ["Link"],
      });
    } else if (!showDocuments && !showLinks) {
      newFilters.push({
        fieldUniqueName: "toolTypeLinkOrDocument",
        values: ["none"],
      });
    }

    setFilters(newFilters);
  }, [showDocuments, showLinks, setFilters]);

  const handleToolClick = (tool: Tool) => {
    let finalUrl = tool.webUrl;

    // For documents, use getOpenLink to format the URL properly
    if (tool.fileUniqueId && tool.fileType) {
      finalUrl = getOpenLink({
        webUrl: tool.webUrl,
        fileType: tool.fileType,
      });
    }

    onApply(finalUrl, tool);
    onOpenChange(false);
  };

  return (
    <Dialog open={open} onOpenChange={(_, data) => onOpenChange(data.open)}>
      <DialogSurface>
        <DialogTitle className={classes.dialogtitle}>
          {t("addLinkToTextOrImage")}
        </DialogTitle>
        <DialogBody className={classes.root}>
          {isLoading || isRefetching || !toolsResult ? (
            <Spinner />
          ) : (
            <div className={classes.toolContainer}>
              <div className={classes.searchRow}>
                <VsaSearchBox
                  placeholder={t("searchTool")}
                  defaultValue={query}
                  onSearch={setQuery}
                  disableQueryParameter
                />
                <Button
                  size="medium"
                  onClick={() => setSortByAscending((sort) => !sort)}
                  icon={
                    sortByAscending ? (
                      <TextSortAscending24Regular />
                    ) : (
                      <TextSortDescending24Regular />
                    )
                  }
                  aria-label={
                    sortByAscending ? t("unsortAlpha") : t("sortAlpha")
                  }
                />
              </div>

              <div className={classes.checkBoxGroup}>
                <Checkbox
                  label={t("document")}
                  checked={showDocuments}
                  onChange={() => setShowDocuments((show) => !show)}
                />
                <Checkbox
                  label={t("link")}
                  checked={showLinks}
                  onChange={() => setShowLinks((show) => !show)}
                />
              </div>

              <div className={classes.toolList}>
                {toolsResult.items.map((tool) => (
                  <div
                    key={tool.toolId}
                    className={classes.toolItem}
                    onClick={() => handleToolClick(tool)}
                  >
                    <div className={classes.toolName}>
                      {tool.fileUniqueId ? (
                        <img
                          src={getDocumentIcon(tool.fileType)}
                          alt="type"
                          width={16}
                          height={16}
                          className={classes.alignStart}
                        />
                      ) : (
                        <Link16Regular />
                      )}
                      <Tooltip
                        content={
                          tool.fileUniqueId
                            ? sanitizeFileName(tool.webUrl)
                            : tool.adminDisplayName || ""
                        }
                        relationship="description"
                      >
                        <span>{tool.title}</span>
                      </Tooltip>
                    </div>
                    {selectedTools.some(
                      (selectedTool) => selectedTool.toolId === tool.toolId
                    ) && <Checkmark20Regular />}
                  </div>
                ))}
              </div>

              {toolsResult.numberOfPages > 1 && (
                <Paginate
                  pageCount={toolsResult.numberOfPages}
                  onPageClick={setCurrentPage}
                  disableQueryParameter
                />
              )}
            </div>
          )}
        </DialogBody>
        <DialogActions>
          <Button appearance="secondary" onClick={() => onOpenChange(false)}>
            {t("cancel")}
          </Button>
        </DialogActions>
      </DialogSurface>
    </Dialog>
  );
}
